import { useEffect } from 'react';

import { ENotificationOrderStateCentroid } from '@webtrader/core/api/notifications';
import { EOrderType, EOrderWebsocketMessageType } from '@webtrader/core/api/order';
import { ETradingState } from '@webtrader/core/api/user';
import { mapNotificationOrderStateToAlertTitle } from '@webtrader/core/constants/notification';
import { alertService } from '@webtrader/core/services/alert';
import { authService } from '@webtrader/core/services/auth';
import { companyService, useCompanyShowNotificationOnSocketMessage } from '@webtrader/core/services/company';
import { notificationsService } from '@webtrader/core/services/notifications';
import { orderService } from '@webtrader/core/services/order';
import { userPreferenceService } from '@webtrader/core/services/userPreference';
import { socketService } from '@webtrader/core/socket';
import { EMetatraderMessageType, IMetaTraderReceiver } from '@webtrader/core/socket/topics/metatrader';

export const useMetaTraderTopic = (traderSubscription?: string, isWeb?: boolean) => {
  const shouldShowNotificationOnSocketMessage = useCompanyShowNotificationOnSocketMessage();
  useEffect(() => {
    const onReceive: IMetaTraderReceiver = (message) => {
      if (!message) {
        return;
      }

      switch (message.M) {
        case EMetatraderMessageType.ORDER:
          if (shouldShowNotificationOnSocketMessage) {
            alertService.showAlert({
              type: message.D.CNOS === ENotificationOrderStateCentroid.REJECTED ? 'error' : 'success',
              message: mapNotificationOrderStateToAlertTitle(message.D.CNOS),
              absoluteAlert: true
            });
          }
          orderService.debouncedRefreshOrders(isWeb)({
            refreshOpenOrders: message.D.C === EOrderType.BUY || message.D.C === EOrderType.SELL,
            refreshOrderHistory: message.D.T === EOrderWebsocketMessageType.CLOSE_ORDER
          });
          break;
        case EMetatraderMessageType.NOTIFICATION:
          notificationsService.fetchNotificationSummary();
          break;
        case EMetatraderMessageType.BALANCE:
          authService.updateAccountBalances({
            equity: message.D.E,
            margin: message.D.M,
            balance: message.D.B,
            profitLoss: message.D.PL,
            freeMargin: message.D.FM,
            marginLevel: message.D.ML,
            marginCall: message.D.MC,
            marginStopout: message.D.MSO,
            notional: message.D.N,
            commission: message.D.CM,
            swaps: message.D.S,
            availableWithdraw: message.D.AW,
            tradingState: message.D.TS as ETradingState,
            credit: message.D.CD
          });
          break;
        case EMetatraderMessageType.ONE_CLICK:
          companyService.retrieveCompanyProfile().then(() => {
            userPreferenceService.getOCTUserPreference();
          });
          break;
        default:
          break;
      }
    };

    socketService.topics().metatrader.subscribe({
      receiver: onReceive
    });

    return () => {
      socketService.topics().metatrader.unsubscribe({
        receiver: onReceive
      });
    };
  }, [traderSubscription, shouldShowNotificationOnSocketMessage]); // eslint-disable-line react-hooks/exhaustive-deps
};
